import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import BottomNav from "@/components/BottomNav";
import LoadingScreen from "@/components/LoadingScreen";
import { api } from "@/lib/api";
import { ArrowLeft, Trash2, AlertTriangle, Download, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function LibraryView() {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [credits, setCredits] = useState(0);
  // We fetch the PDF as a blob (with the bearer token attached by the
  // axios interceptor) and feed an object-URL to the <iframe>.
  // An iframe can't send custom headers, so loading the API URL
  // directly returned `{"detail":"Not authenticated"}` rendered as JSON.
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfError, setPdfError] = useState(null);
  // iOS Safari (and most mobile browsers) refuse to render PDFs inside
  // an <iframe>. Detect it once on mount and show download-only UI.
  const [isMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return /iPhone|iPad|iPod|Android/i.test(window.navigator.userAgent) || window.innerWidth < 720;
  });

  // Friendly filename used by both Download and Open-in-new-tab actions.
  const fileName = (item?.title || "OAK-whitepaper").replace(/[^\w\d -]/g, "").slice(0, 80) + ".pdf";

  const downloadPdf = () => {
    if (!pdfUrl) return;
    const a = document.createElement("a");
    a.href = pdfUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };
  const openInNewTab = () => {
    if (!pdfUrl) return;
    // window.open returns null on aggressive popup-blocking — degrade
    // gracefully by piggy-backing on the same blob URL via target=_blank
    // anchor click.
    const a = document.createElement("a");
    a.href = pdfUrl;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  useEffect(() => {
    Promise.all([api.get("/library"), api.get("/credits/balance")])
      .then(([l, b]) => {
        const found = (l.data || []).find((x) => x.item_id === itemId);
        setItem(found || { item_id: itemId, title: "Whitepaper" });
        setCredits(b.data.credits);
      })
      .catch(() => setItem({ item_id: itemId, title: "Whitepaper" }));
  }, [itemId]);

  // Once we know the item exists, pull the PDF bytes through axios so
  // the bearer token rides along, then expose as a blob URL. Revoke the
  // URL on unmount to avoid memory leaks.
  useEffect(() => {
    if (!item) return;
    let revokeUrl = null;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/library/${itemId}/view`, {
          responseType: "blob",
        });
        if (cancelled) return;
        const blob = new Blob([res.data], { type: "application/pdf" });
        revokeUrl = URL.createObjectURL(blob);
        setPdfUrl(revokeUrl);
      } catch (e) {
        if (cancelled) return;
        const code = e?.response?.status;
        if (code === 404) {
          setPdfError("This whitepaper is no longer available. It may have been removed.");
        } else if (code === 401 || code === 403) {
          setPdfError("Your session has expired — please sign in again to view this saved whitepaper.");
        } else {
          setPdfError("We couldn't load this whitepaper right now. Please try again in a moment.");
        }
      }
    })();
    return () => {
      cancelled = true;
      if (revokeUrl) URL.revokeObjectURL(revokeUrl);
    };
  }, [item, itemId]);

  if (!item) return <LoadingScreen />;

  const remove = async () => {
    try {
      await api.delete(`/library/${itemId}`);
      toast.success("Removed");
      navigate("/dashboard");
    } catch (e) {
      toast.error("Failed to remove");
    }
  };

  return (
    <div className="min-h-screen flex flex-col pb-20 md:pb-0" data-testid="library-view-page">
      <Header credits={credits} onOpenAd={() => {}} onOpenCheckout={() => {}} />

      <main className="max-w-6xl w-full mx-auto px-6 md:px-10 py-8 space-y-6 flex-1 flex flex-col">
        <div className="flex items-center justify-between gap-3">
          <Link
            to="/dashboard"
            className="text-xs text-white/60 hover:text-white inline-flex items-center gap-2"
            data-testid="btn-back-dashboard"
          >
            <ArrowLeft size={14} /> Back to dashboard
          </Link>
          <div className="flex items-center gap-3 flex-wrap justify-end">
            <div className="text-white/50 text-xs font-mono-oak hidden sm:block">{item.item_id}</div>
            {pdfUrl && (
              <>
                <button
                  onClick={downloadPdf}
                  className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#00FF9D]/35 bg-[#00FF9D]/[0.06] text-[#9bffcc] hover:bg-[#00FF9D]/[0.14] hover:text-white transition"
                  data-testid="btn-download-pdf"
                  title="Save the PDF to your device"
                >
                  <Download size={12} /> Download
                </button>
                <button
                  onClick={openInNewTab}
                  className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 hover:border-white/30 text-white/70 hover:text-white transition"
                  data-testid="btn-open-new-tab"
                  title="Open in a new tab using your browser's PDF viewer"
                >
                  <ExternalLink size={12} /> Open
                </button>
              </>
            )}
            <button
              onClick={remove}
              className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 hover:border-rose-400/40 text-white/60 hover:text-rose-400 transition"
              data-testid="btn-remove-library-view"
            >
              <Trash2 size={12} /> Remove
            </button>
          </div>
        </div>

        <div>
          <div className="label-eyebrow mb-2">In-app viewer · OAK Library</div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight">{item.title}</h1>
          {item.goal && <div className="text-white/50 text-sm mt-1">Part of: {item.goal}</div>}
        </div>

        <div className="glass p-2 flex-1 min-h-[70vh]" data-testid="library-pdf-frame">
          {pdfError ? (
            <div
              className="flex flex-col items-center justify-center text-center px-6 py-20 rounded-xl bg-[#0b0e14] min-h-[60vh] gap-4"
              data-testid="library-pdf-error"
            >
              <div className="w-12 h-12 rounded-full bg-[#ff7b00]/15 border border-[#ff7b00]/30 flex items-center justify-center text-[#ffb067]">
                <AlertTriangle size={20} />
              </div>
              <div className="font-display text-lg text-white/90 max-w-md leading-snug">{pdfError}</div>
              <div className="flex items-center gap-3 mt-1">
                <Link
                  to="/dashboard"
                  className="text-[12px] px-4 py-2 rounded-full border border-white/15 text-white/80 hover:bg-white/10 transition"
                  data-testid="library-pdf-error-back"
                >
                  Back to dashboard
                </Link>
                <button
                  type="button"
                  onClick={remove}
                  className="text-[12px] px-4 py-2 rounded-full border border-rose-400/30 text-rose-300 hover:bg-rose-400/10 transition"
                  data-testid="library-pdf-error-remove"
                >
                  Remove from library
                </button>
              </div>
            </div>
          ) : pdfUrl ? (
            isMobile ? (
              // Mobile / iOS Safari: iframes refuse to render PDFs reliably.
              // Show a clean "tap to open / download" card instead of a broken viewer.
              <div
                className="flex flex-col items-center justify-center text-center px-6 py-16 rounded-xl bg-[#0b0e14] min-h-[60vh] gap-5"
                data-testid="library-pdf-mobile-card"
              >
                <div className="w-14 h-14 rounded-2xl bg-[#00FF9D]/10 border border-[#00FF9D]/30 flex items-center justify-center text-[#00FF9D]">
                  <Download size={22} />
                </div>
                <div className="font-display text-lg text-white/95 max-w-sm leading-snug">
                  Your whitepaper is ready
                </div>
                <p className="text-[12.5px] text-white/55 leading-relaxed max-w-xs">
                  Mobile browsers can't render PDFs inline. Tap below to open in your phone's PDF viewer or save it to your device.
                </p>
                <div className="flex flex-col sm:flex-row items-center gap-2.5 mt-1 w-full max-w-sm">
                  <button
                    type="button"
                    onClick={openInNewTab}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-[#00FF9D] hover:brightness-110 text-[#0a0f2f] font-display font-semibold text-[13px] transition"
                    data-testid="btn-mobile-open"
                  >
                    <ExternalLink size={14} /> Open whitepaper
                  </button>
                  <button
                    type="button"
                    onClick={downloadPdf}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full border border-white/15 text-white/85 hover:bg-white/10 font-display font-semibold text-[13px] transition"
                    data-testid="btn-mobile-download"
                  >
                    <Download size={14} /> Download PDF
                  </button>
                </div>
              </div>
            ) : (
              <iframe
                title={item.title}
                src={pdfUrl}
                className="w-full h-[80vh] rounded-xl bg-[#0b0e14]"
                style={{ border: 0 }}
              />
            )
          ) : (
            <div
              className="flex items-center justify-center w-full h-[80vh] rounded-xl bg-[#0b0e14] text-white/45 text-sm"
              data-testid="library-pdf-loading"
            >
              Loading whitepaper…
            </div>
          )}
          <div className="pt-3 text-center text-[11px] text-white/35 tracking-wide">
            Stored inside Oak AI Campus · viewable only within this ecosystem.
          </div>
        </div>
      </main>

      <Footer />
      <BottomNav />
    </div>
  );
}
